'use client';

import { API_ROUTES, useApiInfiniteQuery } from '@shared/api-utils';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { PurchasedTicketsState, Tickets } from '../../types';

type TicketRestriction = 'all' | 'subscription' | 'miniticket' | 'norenewal';
type TicketType = 'ticket' | 'syndicate';

type FilterObject = Record<string, string[]>;
type SortObject = Record<string, 'ascending' | 'descending'>;

type TicketsQueryParams = {
  ticketState?: PurchasedTicketsState;
  pageSize?: number;
  restriction?: TicketRestriction;
  filter?: FilterObject;
  sort?: SortObject;
  type?: TicketType;
};

type ApiQueryParams = {
  ticketState?: PurchasedTicketsState;
  pageSize: number;
  pageNumber?: number;
  restriction?: TicketRestriction;
  filter?: string;
  sort?: string;
  type?: TicketType;
};

const DEFAULT_PAGE_SIZE = 20;

/**
 * Serialises map params to the LMAS query format: `["key":value,"key2":value2]`.
 * The API does not accept standard JSON objects (`{...}`).
 */
function toApiMapParam(value: Record<string, unknown>): string {
  const entries = Object.entries(value).map(([key, entryValue]) => `${JSON.stringify(key)}:${JSON.stringify(entryValue)}`);
  return `[${entries.join(',')}]`;
}

/**
 * Custom React hook to fetch purchased lottery tickets from the API with infinite scrolling support.
 * The API supports both GET and POST methods (both are equivalent).
 *
 * @param options - Configuration options for the tickets query
 * @param options.ticketState - Optional state filter. Values: `'open'` | `'closed'`. When omitted, all tickets are returned
 * @param options.pageSize - Number of tickets per page. Defaults to `20`
 * @param options.restriction - Optional restriction. Values: `'all'` | `'subscription'` | `'miniticket'` | `'norenewal'`
 * @param options.filter - Optional filter object (e.g., `{ lottery: ['euroMillions'] }`)
 * @param options.sort - Optional sort object (e.g., `{ submissionDate: 'ascending' }`)
 * @param options.type - Optional type filter. Values: `'ticket'` | `'syndicate'`
 * @returns Object with `tickets` (flattened array), `isLoading`, `error`, `isFetchingNextPage`, `loadMoreRef`, and other infinite query properties
 */
function usePurchasedTickets({ ticketState, pageSize = DEFAULT_PAGE_SIZE, restriction, filter, sort, type }: TicketsQueryParams): Omit<
  UseInfiniteQueryResult<InfiniteData<Tickets | null>, Error>,
  'data'
> & {
  data: Tickets;
  loadMoreRef: React.RefObject<HTMLDivElement | null>;
} {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const queryParams: ApiQueryParams = {
    ...(ticketState && { ticketState }),
    pageSize,
    ...(filter && { filter: toApiMapParam(filter) }),
    ...(sort && { sort: toApiMapParam(sort) }),
    ...(type && { type }),
    ...(restriction && { restriction }),
  };

  const infiniteQuery = useApiInfiniteQuery<Tickets, number>(API_ROUTES.LOTTERIES.PURCHASES, {
    credentials: true,
    params: queryParams,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < pageSize) {
        return undefined;
      }
      return allPages.length;
    },
    initialPageParam: 0,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = infiniteQuery;

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '600px 0px',
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const data = infiniteQuery.data?.pages.flatMap(page => page ?? []) ?? [];

  return {
    ...infiniteQuery,
    data,
    loadMoreRef,
  };
}

export { usePurchasedTickets };
export type { FilterObject, SortObject };
